import { useCallback, useMemo, useState } from 'react';

import type { AdfDocument } from './agentMentionUtils.ts';
import { extractAgentMentionFromAdf } from './agentMentionUtils.ts';

export type AgentInfo = {
	id: string;
	name: string;
};

/**
 * Hook that tracks whether the current comment ADF contains an agent mention,
 * intended to drive the visibility and content of the FYI nudge above the editor.
 *
 * Call `onContentChange` from the editor's content-change callback.
 * Read `showFyi` to decide whether to render `<AgentMentionFyiNudge />`.
 * Read `agent` for the mentioned agent's identity.
 */
export function useAgentMentionFyiNudge({ enabled = true }: { enabled?: boolean } = {}) {
	const [showFyi, setShowFyi] = useState(false);
	const [agent, setAgent] = useState<AgentInfo | undefined>();

	const onContentChange = useCallback(
		(adf: AdfDocument | undefined) => {
			if (!enabled) {
				return;
			}

			const mentionInfo = extractAgentMentionFromAdf(adf);

			setShowFyi(!!mentionInfo);
			setAgent((prev) => {
				if (!mentionInfo) {
					return undefined;
				}

				if (prev?.id === mentionInfo.agentId && prev?.name === mentionInfo.agentName) {
					return prev;
				}

				return { id: mentionInfo.agentId, name: mentionInfo.agentName };
			});
		},
		[enabled],
	);

	return useMemo(() => ({ showFyi, agent, onContentChange }), [showFyi, agent, onContentChange]);
}
